/* eslint no-console: off */
/* eslint import/no-named-as-default: off */
/* eslint import/no-named-as-default-member: off */
import { useLoadScript } from '@react-google-maps/api';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import ReactDatePicker from 'react-datepicker';
import { FaRegCalendar } from 'react-icons/fa';
import PhoneNumberInput from '../PhoneNumberInput';
import PlacesAutocomplete from '../PlaceAutoComplete';
import styles from './InputBooking.module.scss';

const DateCustomInput = ({
    register,
    onChange,
    placeholder,
    value,
    isSecure,
    id,
    onClick,
    disableByReturnDate,
}) => {
    const ICON_COLOR = '#DAA520';
    return (
        <>
            <FaRegCalendar
                style={{ color: disableByReturnDate ? '#e8e8e8' : ICON_COLOR }}
            />
            <input
                className={
                    disableByReturnDate ? styles.inputDisabled : styles.input
                }
                disabled={disableByReturnDate}
                onChange={onChange}
                placeholder={placeholder}
                value={value}
                isSecure={isSecure}
                id={id}
                onClick={onClick}
                {...register}
            />
        </>
    );
};

const InputBooking = (props) => {
    const { register, disableByReturnDate } = props;
    const [startDate, setStartDate] = useState(dayjs().toDate());
    const [startTime, setStartTime] = useState(dayjs().toDate());
    const [selectId, setSelectId] = useState(null);
    const [openSelectOption, setOpenSelectOption] = useState(false);

    const libraries = useMemo(() => ['places'], []);
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.GCP_GOOGLE_MAP_API_KEY,
        libraries: libraries,
    });

    const callbackValuePlaceInput = (val) => {
        props.onChange(register.name, val);
    };

    const callbackValuePhoneInput = (val, data, formattedValue) => {
        props.onChange(register.name, {
            val,
            data,
            formattedValue,
        });
    };

    const onChangeDate = (date) => {
        const valueDate = dayjs(date).format('YYYY-MM-DD');
        props.onChange(register.name, valueDate);
        setStartDate(date);
    };

    const onChangeTime = (time) => {
        const valueTime = dayjs(time).format('HH:mm');
        props.onChange(register.name, valueTime);
        setStartTime(time);
    };

    useEffect(() => {
        if (props.type === 'datepicker' && props?.defaultDate) {
            setStartDate(dayjs(props.defaultDate).toDate());
        }

        if (
            props.type === 'datepicker' &&
            props?.defaultDateTime &&
            props?.timeOnly
        ) {
            if (props?.timeOnly) {
                const timeValue = dayjs(
                    props.defaultDateTime,
                    'YYYY-MM-DD HH:mm'
                ).toDate();
                setStartTime(timeValue);
            }
        }

        if (props.type === 'select' && props?.defaultValue) {
            setSelectId(props.defaultValue);
        }
    }, [props.type, props.defaultDate]);

    const setSelect = (car) => {
        setSelectId(car.id);
        setOpenSelectOption(false);
        props.onChange(props.name, car);
    };

    const setShowSelectOption = () => {
        setOpenSelectOption(true);
    };

    const refShowOption = useOnclickOutside(() => {
        setOpenSelectOption(false);
    });

    const _renderInput = (type) => {
        if (type === 'auto-place-map') {
            if (!isLoaded) {
                return null;
            } else {
                return (
                    <>
                        <PlacesAutocomplete
                            name={register.name}
                            isLoaded={isLoaded}
                            label={props?.label}
                            placeholder={props.label}
                            required={props.required}
                            defaultValue={props.defaultValue || ''}
                            lat={props?.lat || null}
                            lng={props?.lng || null}
                            callbackValue={callbackValuePlaceInput}
                        />
                        {props?.errorPlace?.[register.name] && (
                            <span className={styles.textRequired}>
                                {`Required ${props.label}`}
                            </span>
                        )}
                    </>
                );
            }
        } else if (type === 'datepicker') {
            return (
                <>
                    <label className={styles.label}>
                        {props?.label}
                        {props?.required ? (
                            <span className={styles.requiredTextColor}>*</span>
                        ) : null}
                    </label>
                    {!props?.timeOnly ? (
                        <ReactDatePicker
                            wrapperClassName="datePicker"
                            selected={startDate}
                            customInput={
                                <DateCustomInput
                                    register={register}
                                    disableByReturnDate={disableByReturnDate}
                                />
                            }
                            onChange={(date) => onChangeDate(date)}
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            dateFormat={'yyyy-MM-dd'}
                            minDate={startDate}
                            readOnly={disableByReturnDate}
                        />
                    ) : (
                        <ReactDatePicker
                            selected={startTime}
                            onChange={(date) => onChangeTime(date)}
                            excludeOut
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={30}
                            dateFormat="HH:mm"
                            timeCaption="time"
                            customInput={
                                <DateCustomInput register={register} />
                            }
                            showTimeSelectOnly={true}
                        />
                    )}
                    {props?.errors?.[register.name] && (
                        <span className={styles.textRequired}>
                            {props?.errors?.[register.name]?.message}
                        </span>
                    )}
                </>
            );
        } else if (type === 'phone') {
            return (
                <>
                    <label className={styles.label}>
                        {props?.label}
                        {props?.required ? (
                            <span className={styles.requiredTextColor}>*</span>
                        ) : null}
                    </label>
                    <PhoneNumberInput
                        inputProps={{
                            name: register.name,
                            required: true,
                            autoFocus: true,
                        }}
                        defaultValue={props.defaultValue}
                        countryCodeEditable={false}
                        callbackValuePhoneInput={callbackValuePhoneInput}
                    />
                    {props?.errorPhone?.[register.name] && (
                        <span className={styles.textRequired}>
                            {`Invalid ${props.label}, must be 10 digits `}
                        </span>
                    )}
                </>
            );
        } else if (type === 'email') {
            return (
                <>
                    <label className={styles.label}>
                        {props?.label}
                        {props?.required ? (
                            <span className={styles.requiredTextColor}>*</span>
                        ) : null}
                    </label>
                    <input
                        className={styles.input}
                        placeholder={
                            props?.placeholder ? props.placeholder : props.label
                        }
                        defaultValue={props.defaultValue || ''}
                        {...register}
                    />
                    {props?.errors?.[register.name] && (
                        <span className={styles.textRequired}>
                            {props?.validError
                                ? `${props?.label} is not valid`
                                : `Required ${props.label}`}
                        </span>
                    )}
                </>
            );
        } else if (type === 'select') {
            return (
                <>
                    <label className={styles.label}>
                        {props?.label}
                        {props?.required ? (
                            <span className={styles.requiredTextColor}>*</span>
                        ) : null}
                    </label>
                    <div
                        ref={refShowOption}
                        className={styles.inputSelect}
                        onClick={() => setShowSelectOption()}
                    >
                        {selectId
                            ? props.data.find((d) => d.id === selectId).name
                            : props?.defaultTextSelect || 'Select'}
                    </div>
                    <div
                        ref={refShowOption}
                        className={`${styles.optionDetail} ${
                            openSelectOption ? styles.showOption : ''
                        }`}
                    >
                        <div className={styles.wrapOption}>
                            {props.data.map((d, index) => (
                                <div className={styles.listOption} key={index}>
                                    <button
                                        className={styles.btn}
                                        onClick={() => setSelect(d)}
                                        type="button"
                                    >
                                        {d.name}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                    {props?.errors?.[props.name] && (
                        <span className={styles.textRequired}>
                            {`Required Select ${props.label}`}
                        </span>
                    )}
                </>
            );
        } else if (type === 'message') {
            let inputType = '';
            if (type === 'password') {
                inputType = 'password';
            } else if (type === 'number') {
                inputType = 'number';
            } else {
                inputType = 'text';
            }
            return (
                <>
                    <label className={styles.label}>
                        {props?.label}
                        {props?.required ? (
                            <span className={styles.requiredTextColor}>*</span>
                        ) : null}
                    </label>
                    <textarea
                        className={styles.input}
                        placeholder={
                            props?.placeholder ? props.placeholder : props.label
                        }
                        type={inputType}
                        defaultValue={props.defaultValue || ''}
                        {...register}
                        maxLength={250}
                    />
                    {props?.errors?.[register.name] && (
                        <span className={styles.textRequired}>
                            {props?.errors?.[register.name]?.message}
                        </span>
                    )}
                </>
            );
        } else {
            let inputType = '';
            if (type === 'password') {
                inputType = 'password';
            } else if (type === 'number') {
                inputType = 'number';
            } else {
                inputType = 'text';
            }
            return (
                <>
                    <label className={styles.label}>
                        {props?.label}
                        {props?.required ? (
                            <span className={styles.requiredTextColor}>*</span>
                        ) : null}
                    </label>
                    <input
                        className={styles.input}
                        placeholder={
                            props?.placeholder ? props.placeholder : props.label
                        }
                        type={inputType}
                        defaultValue={props.defaultValue || ''}
                        {...register}
                    />
                    {props?.errors?.[register.name] && (
                        <span className={styles.textRequired}>
                            {props?.errors?.[register.name]?.message}
                        </span>
                    )}
                </>
            );
        }
    };
    return <>{_renderInput(props?.type)}</>;
};

InputBooking.propTypes = {
    register: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    label: PropTypes.string.isRequired,
    required: PropTypes.bool,
};

export default InputBooking;
